import { useState, useRef, useEffect } from 'react';
import { SharePointData } from '../../types/sharepoint.types';
import { triggerPiepeline, PipelineRun, cancelPipeline } from '../../services/api/virtualAssistant';
import { Pipeline } from '../../types/pipelineData';
import { fetchSharePointItems, getCachedData } from './sharepointHelpers';
import { createSharePointConnection, URLTargetingUtils, type URLAnalysis, type EnhancedConnectionResponse } from '../../services/api/sharepointService';

// Helper functions for smart duplicate checking
interface UseSharePointConnectionProps {
  sharepoints: SharePointData[];
  setSharepoints: React.Dispatch<React.SetStateAction<SharePointData[]>>;
  setCheckedItems: React.Dispatch<React.SetStateAction<string[]>>;
  setIsLoadingTabs: React.Dispatch<React.SetStateAction<{ [key: number]: { [key: string]: boolean } }>>;
  assistant: any;
  triggerAutoSave: () => void;
  isSharePointFullLoad: boolean;
  connectUsing: string;
  setConnectUsing: (value: string) => void;
  connectedSharePointIds: string[];
  setConnectedSharePointIds: React.Dispatch<React.SetStateAction<string[]>>;
  setPendingSave: (value: boolean) => void;
  isGettingDataCalled: boolean;
  setActiveTab: (sharepointId: number, tab: 'CONNECT' | 'PREVIEW') => void;
  setCancellingPipeline: React.Dispatch<React.SetStateAction<boolean>>; // NEW: Cancel state setter
}

// Helper functions for smart duplicate checking
const isDuplicateConnection = (newUrl: string, newData: any, existingSharepoints: SharePointData[]): boolean => {
  console.log('🔍 Checking for duplicates:', {
    newUrl,
    newResourceType: newData.resource_type,
    newDrillDown: newData.drill_down_info,
    existingCount: existingSharepoints.length
  });

  const connectedSites = existingSharepoints.filter(s => s.sharepoint_id !== "");
  
  for (const existing of connectedSites) {
    console.log('🔍 Comparing with existing:', {
      existingUrl: existing.url,
      existingAnalysis: existing.url_analysis,
      existingType: existing.url_type
    });

    if (!existing.url_analysis && !existing.url_type) {
      if (existing.url.trim() === newUrl.trim()) {
        console.log('❌ Duplicate found: Exact URL match with legacy connection');
        return true;
      }
      continue;
    }

    const existingAnalysis = existing.url_analysis || {
      url_type: existing.url_type,
      target_list_id: existing.target_list_id,
      target_library_id: existing.target_library_id,
      target_folder_path: existing.target_folder_path
    };

    // Convert new data to analysis format for comparison
    const newAnalysis = {
      url_type: newData.resource_type === 'document_library' ? 'library' : newData.resource_type,
      target_list_id: newData.drill_down_info?.list_id,
      target_library_id: newData.drill_down_info?.drive_id,
      target_folder_path: newData.drill_down_info?.folder_path
    };

    if (areTargetingConflicts(newAnalysis, existingAnalysis)) {
      console.log('❌ Duplicate found: Targeting conflict detected');
      return true;
    }
  }

  console.log('✅ No duplicates found');
  return false;
};

const areTargetingConflicts = (analysis1: any, analysis2: any): boolean => {
  if (analysis1.url_type === 'site' && analysis2.url_type === 'site') {
    return false; // Allow multiple site connections
  }

  if (analysis1.url_type === analysis2.url_type) {
    switch (analysis1.url_type) {
      case 'list':
        return analysis1.target_list_id === analysis2.target_list_id;
      case 'library':
        return analysis1.target_library_id === analysis2.target_library_id;
      case 'folder':
        return (
          analysis1.target_library_id === analysis2.target_library_id &&
          analysis1.target_folder_path === analysis2.target_folder_path
        );
      default:
        return false;
    }
  }

  // Check for hierarchical conflicts
  if (analysis1.url_type === 'library' && analysis2.url_type === 'folder') {
    return analysis1.target_library_id === analysis2.target_library_id;
  }
  
  if (analysis1.url_type === 'folder' && analysis2.url_type === 'library') {
    return analysis1.target_library_id === analysis2.target_library_id;
  }

  return false;
};

const getDuplicateErrorMessage = (newData: any, existingSharepoints: SharePointData[]): string => {
  const conflictingConnection = existingSharepoints.find(existing => {
    if (!existing.url_analysis && !existing.url_type) return false;
    
    const existingAnalysis = existing.url_analysis || {
      url_type: existing.url_type,
      target_list_id: existing.target_list_id,
      target_library_id: existing.target_library_id,
      target_folder_path: existing.target_folder_path
    };
    
    const newAnalysis = {
      url_type: newData.resource_type === 'document_library' ? 'library' : newData.resource_type,
      target_list_id: newData.drill_down_info?.list_id,
      target_library_id: newData.drill_down_info?.drive_id,
      target_folder_path: newData.drill_down_info?.folder_path
    };
    
    return areTargetingConflicts(newAnalysis, existingAnalysis);
  });

  if (!conflictingConnection) {
    return 'This SharePoint target is already connected.';
  }

  const existingName = conflictingConnection.sharepoint_name || 'Unknown Site';
  
  switch (newData.resource_type) {
    case 'list':
      return `This SharePoint list is already connected in "${existingName}".`;
    case 'document_library':
      return `This SharePoint library is already connected in "${existingName}".`;
    case 'folder':
      return `This SharePoint folder is already connected in "${existingName}".`;
    case 'site':
      return `This SharePoint site is already connected as "${existingName}".`;
    default:
      return `This SharePoint target is already connected in "${existingName}".`;
  }
};

export const useSharePointConnection = ({
  sharepoints,
  setSharepoints,
  setCheckedItems,
  setIsLoadingTabs,
  assistant,
  triggerAutoSave,
  isSharePointFullLoad,
  connectUsing,
  setConnectUsing,
  connectedSharePointIds,
  setConnectedSharePointIds,
  setPendingSave,
  isGettingDataCalled,
  setActiveTab,
  setCancellingPipeline // NEW: Cancel state setter
}: UseSharePointConnectionProps) => {
  const fetchCalledRef = useRef<{ [key: number]: boolean }>({});
  const [systemConfig, setSystemConfig] = useState<{ Id: string; ConfigValue: string; }[]>([]);

  // Pipeline states
  const [pipelineData, setPipelineData] = useState<Pipeline[]>([]);
  const [gettingData, setGettingData] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<null | {
    progress: number;
    status: string;
    error?: string;
  }>(null);
  const [getDataClickTime, setGetDataClickTime] = useState<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Refresh states
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load system config
  useEffect(() => {
    const config = getCachedData('systemConfig');
    if (config) {
      setSystemConfig(config);
    }
  }, []);

  // UPDATED: Pipeline data fetching with cancel states and click time filtering
  const fetchPipelineData = async () => {
    const pipeline_name = "sharepoint_to_vectordb";
    if (assistant.app_id) {
      const resPipelineRun = await PipelineRun(pipeline_name, assistant.app_id);

      // ALWAYS store ALL pipelines for history display
      setPipelineData(resPipelineRun);

      // Filter: Only consider pipelines created AFTER user clicked "Get Data Now" for STATUS determination
      let relevantPipeline = resPipelineRun;
      if (getDataClickTime && resPipelineRun.length > 0) {
        relevantPipeline = resPipelineRun.filter((p: Pipeline) => {
          const pipelineCreatedAt = new Date(p.created_ts);
          return pipelineCreatedAt >= getDataClickTime;
        });

        // If no new pipelines found yet, keep polling but don't show old status
        if (relevantPipeline.length === 0 && gettingData) {
          setCurrentStatus({ progress: 20, status: "In Progress" });
          return;  // Early return - pipelineData still has all history
        }
      }

      // Use relevantPipeline ONLY for status determination
      if (relevantPipeline && relevantPipeline.length > 0) {
        const backendStatus = relevantPipeline[0].status;
        let status = '';

        // Map backend status to UI status - ALWAYS check actual status first
        if (backendStatus === 'STARTED') status = 'In Progress';
        if (backendStatus === 'failed') status = 'Failed';
        if (backendStatus === 'success') status = 'Complete';
        if (backendStatus === 'cancelled') {
          status = 'Cancelled';
          setCancellingPipeline(false);
          setGettingData(false);
        }
        if (backendStatus === 'cancelling') {
          status = 'Cancelling';
          setCancellingPipeline(true);
        }
        if (backendStatus === 'cancel_failed') {
          status = 'Failed';
          setCancellingPipeline(false);
        }

        setCurrentStatus({ progress: 20, status: status });
      } else {
        if (gettingData) {
          setCurrentStatus({ progress: 20, status: "In Progress" });
        } else {
          setCurrentStatus({ progress: 0, status: "Idle" });
        }
      }
    }
  };

  // UPDATED: Pipeline polling logic to stop on cancelled
  useEffect(() => {
    fetchPipelineData();
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // UPDATE CONDITION TO INCLUDE CANCEL STATES
    if (currentStatus?.status !== 'Complete' && currentStatus?.status !== 'Cancelled') {
      if (isGettingDataCalled) {
        intervalRef.current = setInterval(() => {
          fetchPipelineData();
        }, 10 * 2000); // 10 seconds
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentStatus?.status, isGettingDataCalled, assistant.app_id]);

  // NEW: Cancel pipeline function
  const handleCancelPipeline = async () => {
    try {
      setCancellingPipeline(true);
      setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'Cancelling' }));

      await cancelPipeline("sharepoint_to_vectordb", assistant.app_id);

      // Poll for status update after cancel request
      setTimeout(() => {
        fetchPipelineData();
      }, 2000);
    } catch (error) {
      console.error('Failed to cancel pipeline:', error);
      setCancellingPipeline(false);
      setCurrentStatus(prev => ({ ...prev, progress: 20, status: 'In Progress' }));
      
      // Re-throw error to be handled by the parent component
      throw error;
    }
  };

  // SharePoint connection monitoring effect
  useEffect(() => {
    const sharepointid_prefix = systemConfig.find(item => item.Id === 'sharepoint_siteid_prefix')?.ConfigValue;
    
    if (sharepoints.length > 0) {
      sharepoints.forEach((sharepoint) => {
        if (!sharepoint.sharepoint_id && !sharepoint.connectionState) {
          setSharepoints(prev => prev.map(s =>
            s.id === sharepoint.id ? { ...s, connectionState: 'idle', activeTab: 'CONNECT' } : s
          ));
        }
        if (sharepoint.sharepoint_id && sharepoint.connectionState !== 'connected') {
          setSharepoints(prev => prev.map(s =>
            s.id === sharepoint.id ? { ...s, connectionState: 'connected', activeTab: 'CONNECT' } : s
          ));
        }
        if (sharepoint.connectionState === 'connected' && !fetchCalledRef.current[sharepoint.id]) {
          let sharepointUrlSegment = sharepoint.url.split('/').filter(Boolean).pop();
          if (sharepointUrlSegment) {
            if (sharepointUrlSegment?.length < 7) {
              sharepointUrlSegment = sharepointid_prefix + sharepointUrlSegment;
            }
            const finalSegment = sharepointUrlSegment;
            setConnectedSharePointIds((prev) => [...prev, finalSegment]);
          }
          if (connectUsing === 'click') triggerAutoSave();

          const urlAnalysis = sharepoint.url_analysis || {
            url_type: 'site',
            target_list_id: undefined,
            target_library_id: undefined,
            target_folder_path: undefined
          };

          fetchSharePointItems(
            sharepoint.id,
            sharepoints,
            setSharepoints,
            setCheckedItems,
            setIsLoadingTabs,
            assistant,
            false,
            urlAnalysis
          );
          fetchCalledRef.current[sharepoint.id] = true;
        }
      });
    }
  }, [sharepoints, systemConfig, connectUsing, triggerAutoSave, setSharepoints, setCheckedItems, setIsLoadingTabs, assistant, setConnectedSharePointIds]);

  // UPDATED: Connection handler with standardized backend response
  const handleConnect = async (sharepointId: number, source: 'click' | 'loop') => {
    if (source === 'click') {
      setConnectUsing('click');
    }
    
    const sharepoint = sharepoints.find(s => s.id === sharepointId);
    if (!sharepoint?.url) {
      setSharepoints((prev) =>
        prev.map((s) =>
          s.id === sharepointId
            ? { ...s, UrlError: 'Please enter a valid URL.' }
            : s
        )
      );
      return;
    }

    // Clear any existing errors
    setSharepoints((prev) =>
      prev.map((s) =>
        s.id === sharepointId ? { ...s, UrlError: undefined } : s
      )
    );

    // Set connecting state
    setSharepoints(prev => prev.map(sharepoint =>
      sharepoint.id === sharepointId
        ? { ...sharepoint, connectionState: 'connecting' }
        : sharepoint
    ));

    try {
      console.log('🔗 Attempting SharePoint connection:', sharepoint.url);
      
      // Create connection and get standardized response
      const result: EnhancedConnectionResponse = await createSharePointConnection(
        sharepoint.url,
        'test'
      );

      console.log('🔍 Full backend response:', result);
      console.log('🔍 Result.data:', result.data);
      console.log('🔍 Result.connection_info:', (result as any).connection_info);

      if (result.connection_status === 'success' && result.data) {
        // NEW: Smart duplicate check based on standardized response
        const isDuplicate = isDuplicateConnection(sharepoint.url, result.data, sharepoints);
        
        if (isDuplicate) {
          const errorMessage = getDuplicateErrorMessage(result.data, sharepoints);
          
          setSharepoints((prev) =>
            prev.map((s) =>
              s.id === sharepointId
                ? { 
                    ...s, 
                    UrlError: errorMessage,
                    connectionState: 'idle'
                  }
                : s
            )
          );
          return;
        }

        // Extract targeting information from standardized response
        const resourceType = result.data.resource_type;
        const drillDownInfo = result.data.drill_down_info || {};
        
        // Convert to URL analysis format for backward compatibility
        const urlAnalysis: URLAnalysis = {
          url_type: resourceType === 'document_library' ? 'library' : resourceType,
          target_list_id: drillDownInfo.list_id,
          target_library_id: drillDownInfo.drive_id,
          target_folder_path: drillDownInfo.folder_path
        };

        // Log targeting information
        if (URLTargetingUtils.isTargeted(urlAnalysis)) {
          console.log('🎯 Targeted SharePoint URL detected:', {
            type: urlAnalysis.url_type,
            targetListId: urlAnalysis.target_list_id,
            targetLibraryId: urlAnalysis.target_library_id,
            folderPath: urlAnalysis.target_folder_path
          });
        }

        // Extract site ID from connection_info (CRITICAL FIX) - using type assertion due to interface mismatch
        const connectionInfo = (result as any).connection_info;
        const siteId = connectionInfo?.resolved_site_id || drillDownInfo.site_id || result.data.id;

        // Update SharePoint state with connection success and standardized data
        setSharepoints(prev => prev.map(s =>
          s.id === sharepointId
            ? { 
                ...s, 
                connectionState: 'connected', 
                sharepoint_id: siteId,
                sharepoint_name: result.data.name, 
                tenant: result.tenant,
                // Add this line in the handleConnect function:
                site_name: (result as any).parent_info?.site_name || connectionInfo?.resolved_site_name,
                // Store URL analysis metadata for backward compatibility
                url_type: urlAnalysis.url_type,
                target_list_id: urlAnalysis.target_list_id,
                target_library_id: urlAnalysis.target_library_id,
                target_folder_path: urlAnalysis.target_folder_path,
                original_url: connectionInfo?.original_url || sharepoint.url,
                connection_method: connectionInfo?.extraction_method || result.resolution_method,
                url_analysis: urlAnalysis
              }
            : s
        ));

        // Navigate to preview after successful connection
        setTimeout(() => {
          setActiveTab(sharepointId, 'PREVIEW');
          if (source === 'click') {
            setPendingSave(true);
          } else if (!isGettingDataCalled) {
            setPendingSave(true);
          }
        }, 2000);

      } else {
        const errorMessage = result.message || 'Connection failed';
        throw new Error(errorMessage);
      }

    } catch (error) {
      console.error('❌ SharePoint connection failed:', error);
      
      // Reset connection state on error
      setSharepoints(prev => prev.map((sharepoint) =>
        sharepoint.id === sharepointId
          ? { 
              ...sharepoint, 
              connectionState: 'idle', 
              UrlError: error instanceof Error 
                ? error.message 
                : 'Failed to connect. Please try again.'
            }
          : sharepoint
      ));
    }
  };

  // Enhanced refresh SharePoint data handler with URL analysis support
  const handleRefreshSharePointData = async (sharepointId: number) => {
    console.log('Starting SharePoint data refresh for ID:', sharepointId);
    
    if (isRefreshing) {
      console.log('Refresh already in progress, skipping...');
      return;
    }

    setIsRefreshing(true);
    
    try {
      const sharepoint = sharepoints.find(s => s.id === sharepointId);
      if (!sharepoint) {
        throw new Error('SharePoint not found');
      }

      if (sharepoint.connectionState !== 'connected') {
        throw new Error('SharePoint is not connected');
      }

      // Clear any drill-down state before refresh
      setSharepoints(prev => prev.map(s =>
        s.id === sharepointId 
          ? { 
              ...s, 
              drillDownState: undefined,
              error: undefined 
            } 
          : s
      ));

      const urlAnalysis = sharepoint.url_analysis || {
        url_type: 'site',
        target_list_id: sharepoint.target_list_id,
        target_library_id: sharepoint.target_library_id,
        target_folder_path: sharepoint.target_folder_path
      };

      console.log('🔄 Refreshing with URL analysis:', urlAnalysis);

      await fetchSharePointItems(
        sharepointId,
        sharepoints,
        setSharepoints,
        setCheckedItems,
        setIsLoadingTabs,
        assistant,
        true,
        urlAnalysis
      );

      console.log('SharePoint data refresh completed successfully');
      
    } catch (error) {
      console.error('SharePoint data refresh failed:', error);
      
      setSharepoints(prev => prev.map(s =>
        s.id === sharepointId 
          ? { 
              ...s, 
              error: error instanceof Error 
                ? error.message 
                : 'Failed to refresh data. Please try again.'
            } 
          : s
      ));
      
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  };

  // Get data now handler
  const handleGetDataNow = async (type: 'azure' | 'azure') => {
    console.log('Starting Get Data Now process...');
    setGettingData(true);
    setPendingSave(false);
    setGetDataClickTime(new Date());
    setCurrentStatus({ progress: 20, status: 'In Progress' });
    
    let queue_name = systemConfig.find(item => item.Id === 'sharepoint_queue_name')?.ConfigValue ?? '';
    if (type === 'azure') {
      queue_name = systemConfig.find(item => item.Id === 'sharepoint_queue_name')?.ConfigValue ?? '';
    }
    
    const run_type = 'Adhoc';
    const full_load_value = String(isSharePointFullLoad) === 'true' ? 'false' : 'true';
    
    if (queue_name) {
      try {
        const res = await triggerPiepeline(assistant.app_id, run_type, queue_name, full_load_value);
        console.log('Pipeline triggered successfully:', res);
        
        setTimeout(() => {
          fetchPipelineData();
        }, 30000);
      } catch (error) {
        console.error('Failed to trigger pipeline:', error);
        setCurrentStatus({ progress: 0, status: 'Failed', error: 'Failed to start pipeline' });
        setGettingData(false);
      }
    } else {
      console.error('Queue name not found in system config');
      setCurrentStatus({ progress: 0, status: 'Failed', error: 'Configuration error' });
      setGettingData(false);
    }
  };

  // Stop polling function
  const stopPolling = () => {
    console.log('Manually stopping pipeline polling');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setGettingData(false);
    setCurrentStatus({ progress: 0, status: 'Idle' });
  };

  return {
    // States
    systemConfig,
    pipelineData,
    currentStatus,
    gettingData,
    getDataClickTime,
    isRefreshing,
    
    // Handlers
    handleConnect,
    handleGetDataNow,
    handleRefreshSharePointData,
    handleCancelPipeline, // NEW: Cancel handler
    fetchPipelineData,
    stopPolling,
    
    // Refs
    fetchCalledRef
  };
};